import { useLang } from "./OneHealthContext";

const asset = (p) => `${process.env.PUBLIC_URL || ""}${p}`;

/* Readiness mark — outline ring / half disc / solid disc.
   The shape carries the meaning; a text label always accompanies it. */
export function ReadinessMark({ verdict, lg }) {
  return <span className={`oh-mark ${verdict}${lg ? " lg" : ""}`} aria-hidden="true" />;
}

/* L&T brand lockup — sits in the top bezel. */
export function LTBrand() {
  return <img className="oh-lt-logo" src={asset("/brand/lt-logo.png")} alt="Larsen & Toubro" />;
}

/* Powered-by CarePlix lockup — sits in the top bezel (right side). */
export function PoweredByCarePlix() {
  const { tr } = useLang();
  return (
    <div className="oh-powered">
      <span className="pb">{tr("common.poweredByShort")}</span>
      <img className="oh-cp-logo" src={asset("/brand/careplix-logo.png")} alt="CarePlix" />
    </div>
  );
}

/* Three-segment band meter (0 normal · 1 elevated · 2 high) for BP / stress. */
export function BandBar({ level }) {
  return (
    <div className="oh-bandbar" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <i key={i} className={i <= level ? `on${level}` : ""} />
      ))}
    </div>
  );
}

/* RAG status icon — a filled disc + a glyph: tick (green) / warning (amber) /
   cross (red) / dash (n-a). Colour is reinforced by the glyph shape, so meaning
   survives for colour-blind users. */
export function StatusIcon({ status = "na", size = 22 }) {
  const p = { width: size, height: size, viewBox: "0 0 24 24" };
  const glyph = {
    green: <path d="M6.5 12.4l3.4 3.4L17.6 8.2" />,
    amber: <><path d="M12 6.6v6.6" /><circle className="dot" cx="12" cy="17" r="1.25" /></>,
    red: <path d="M8 8l8 8M16 8l-8 8" />,
    na: <path d="M8 12h8" />,
  }[status] || <path d="M8 12h8" />;
  return (
    <svg className={`oh-ico ${status}`} {...p} aria-hidden="true">
      <circle className="bg" cx="12" cy="12" r="11" />
      {glyph}
    </svg>
  );
}

/* Fit-for-work verdict badge — big RAG disc + glyph. */
export function VerdictBadge({ status, size = 76 }) {
  return (
    <div className="oh-verdict-badge" aria-hidden="true">
      <StatusIcon status={status} size={size} />
    </div>
  );
}

/* Score ring (0–100) with the value in the middle, coloured by RAG status. */
export function ScoreRing({ value, status = "na", size = 76 }) {
  const pct = value == null ? 0 : Math.max(0, Math.min(100, value));
  return (
    <svg className={`oh-sring ${status}`} viewBox="0 0 42 42" width={size} height={size} aria-hidden="true">
      <circle className="ring-bg" cx="21" cy="21" r="15.915" />
      {value != null && (
        <circle
          className="ring-fg"
          cx="21" cy="21" r="15.915"
          strokeDasharray={`${pct} ${100 - pct}`}
          strokeDashoffset="25"
        />
      )}
      <text className="ring-val" x="21" y="21" dominantBaseline="central" textAnchor="middle">
        {value == null ? "–" : value}
      </text>
    </svg>
  );
}
